import { ConnectionPositionPair, Overlay, OverlayConfig } from '@angular/cdk/overlay';
import { ComponentPortal, PortalInjector } from '@angular/cdk/portal';
import {
    Component,
    ElementRef,
    EventEmitter,
    InjectionToken,
    Injector,
    Input,
    OnChanges,
    OnDestroy,
    Output,
} from '@angular/core';
import { BehaviorSubject, of, Subject } from 'rxjs';
import { concatMap, take, takeUntil } from 'rxjs/operators';
import { AddLabelModalComponent } from '../add-label-modal/add-label-modal.component';
import { EditLabelModalComponent } from '../edit-label-modal/edit-label-modal.component';
import { LabelService } from '../label.service';
import { Label } from '../model/label';
import { ELabelType, ILabel } from 'src/app/models/label';
import { ToolsService } from 'src/app/services/tools.service';

export const LABEL_PARAMS = new InjectionToken<any>('LABEL_PARAMS');

@Component({
    selector: 'app-label-list',
    templateUrl: './label-list.component.html',
    styleUrls: ['./label-list.component.scss'],
})
export class LabelListComponent implements OnDestroy, OnChanges {
    @Input()
    orderId: string;

    @Input()
    labels: any[];

    @Input() type = ELabelType.OrderBoard;

    @Output()
    labelsSelected: EventEmitter<{ labels: ILabel[]; entityId: string | number }> = new EventEmitter();

    labels$ = new BehaviorSubject<any[]>([]);

    editLabel$ = new BehaviorSubject<Partial<Label>>(undefined);
    closeAllModals$ = new Subject<boolean>();
    singleLabelUpdated$ = new Subject<void>();

    isLoading$ = new BehaviorSubject(false);

    destroy$ = new Subject<void>();

    entityTmpId;

    constructor(
        private labelService: LabelService,
        private overlay: Overlay,
        private injector: Injector,
        private elRef: ElementRef,
        private toolsService: ToolsService
    ) {}

    ngOnInit() {
        if (!this.orderId) {
            this.orderId = this.toolsService.newUUID();
        }

        this.labels$.subscribe((labels) => {
            this.labelsSelected.emit({ labels: labels as ILabel[], entityId: this.orderId });
        });
    }

    ngOnChanges() {
        this.labels$.next(this.labels);
        this.isLoading$.next(false);
    }

    unassignLabel(label: Label) {
        this.isLoading$.next(true);
        this.labelService.unassignLabel(label.id, this.orderId).subscribe((data) => {
            this.labels$.next(data);
            this.isLoading$.next(false);
        });
    }

    showAddLabelsModal() {
        this.labels$.pipe(take(1)).subscribe((data) => {
            this.showModal('addLabel', {
                assignedLabels: data,
                orderId: this.orderId,
                type: this.type,
            });
        });
    }

    showEditLabelModal(label: any) {
        this.showModal('editLabel', { label, orderId: this.orderId });
    }

    showModal(modal: 'addLabel' | 'editLabel', data?: any) {
        const positionStrategy = this.overlay
            .position()
            .flexibleConnectedTo(this.elRef)
            .withPositions([
                new ConnectionPositionPair(
                    { originX: 'start', originY: 'top' },
                    {
                        overlayX: 'start',
                        overlayY: 'top',
                    }
                ),
            ]);
        const scrollStrategy = this.overlay.scrollStrategies.reposition();

        const dialogRef = this.overlay.create(
            new OverlayConfig({
                disposeOnNavigation: true,
                scrollStrategy,
                positionStrategy,
                hasBackdrop: false,
                panelClass: 'label-dialog',
                backdropClass: 'label-dialog-wrap',
                height: 214,
            })
        );
        this.destroy$.subscribe((dontCare) => dialogRef.dispose());
        if (modal === 'addLabel') {
            data.singleLabelUpdated$ = this.singleLabelUpdated$;
            const portal = new ComponentPortal(AddLabelModalComponent, null, this.createInjector(data));
            const componentRef = dialogRef.attach(portal);
            componentRef.instance.editLabel$.pipe(take(1), takeUntil(this.destroy$)).subscribe((labelData: ILabel) => {
                dialogRef.dispose();
                labelData.type = this.type;
                this.showEditLabelModal(labelData);
            });
            componentRef.instance.labelListUpdated$
                .pipe(
                    takeUntil(componentRef.instance.close$),
                    takeUntil(this.destroy$),
                    concatMap((newLabels) => {
                        this.labels$.next(newLabels);
                        return of();
                    })
                )
                .subscribe();
            componentRef.instance.close$.pipe(take(1), takeUntil(this.destroy$)).subscribe(() => {
                dialogRef.dispose();
            });
        } else if (modal === 'editLabel') {
            const portal = new ComponentPortal(EditLabelModalComponent, null, this.createInjector(data));
            const componentRef = dialogRef.attach(portal);
            componentRef.instance.createdLabel$.pipe(take(1), takeUntil(this.destroy$)).subscribe(() => {
                dialogRef.dispose();
                this.singleLabelUpdated$.next();
            });
            componentRef.instance.close$.pipe(take(1), takeUntil(this.destroy$)).subscribe(() => {
                dialogRef.dispose();
            });
        }
    }

    private createInjector(params: any): PortalInjector {
        const weakMap = new WeakMap<any, any>([[LABEL_PARAMS, params]]);
        return new PortalInjector(this.injector, weakMap);
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }
}
